import { db } from '@/core/db/dexie'
import { parteDiariaEndpoints } from '@/core/api/endpoints'
import { ApiError } from '@/core/api/fetchClient'
import type { OutParteDiaria, RejeicaoParteDiaria } from '@/core/db/entities'
import type { LancamentoEppDto, LancamentoMveDto } from '@/types/parteDiariaDtos'
import { classificarFalhaSync } from '@/features/carreteiro/carreteiroRegras'
import { RECUSAS_SEM_CORRECAO_PD } from '@/features/parteDiaria/parteDiariaRegras'
import type { PushSummary } from './carreteiroSync'

/**
 * Push das partes diárias pendentes (mesmo desenho do carreteiroSync):
 *  - 1 chamada por registro; o backend grava em transação (MVE: todas as
 *    linhas do dia; EPP: a linha + recálculo do mês).
 *  - 2xx → SYNCED · rede/401/408/429/5xx → PENDING (reenvia) · demais 4xx →
 *    REJEITADA (recusa de negócio; aparece em "Recusadas pelo servidor").
 */

let runningPromise: Promise<PushSummary> | null = null

export async function pushParteDiariaOutbox(): Promise<PushSummary> {
  if (runningPromise) return runningPromise
  runningPromise = doPush().finally(() => {
    runningPromise = null
  })
  return runningPromise
}

async function doPush(): Promise<PushSummary> {
  const summary: PushSummary = { attempted: 0, accepted: 0, failed: 0, rejected: 0, errors: [] }

  await db.out_parte_diaria.where('syncStatus').equals('SYNCING').modify({ syncStatus: 'PENDING' })
  const pendentes = await db.out_parte_diaria.where('syncStatus').anyOf('PENDING', 'FAILED').toArray()
  if (pendentes.length === 0) return summary
  summary.attempted = pendentes.length

  for (const item of pendentes) {
    await db.out_parte_diaria.update(item.id, { syncStatus: 'SYNCING' })
    const now = Date.now()
    try {
      if (item.modulo === 'MVE') await parteDiariaEndpoints.syncMve(buildMveDto(item))
      else await parteDiariaEndpoints.syncEpp(buildEppDto(item))
      await db.out_parte_diaria.update(item.id, {
        syncStatus: 'SYNCED',
        lastSyncAttempt: now,
        failureReason: null,
        rejeicao: null,
      })
      summary.accepted += 1
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0
      const reason = err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message
      if (err instanceof ApiError && classificarFalhaSync(status) === 'REJEITADA') {
        const rejeicao = montarRejeicao(err, !!item.draftState)
        await db.out_parte_diaria.update(item.id, {
          syncStatus: 'REJEITADA',
          lastSyncAttempt: now,
          failureReason: rejeicao.message,
          rejeicao,
          retryCount: (item.retryCount ?? 0) + 1,
        })
        summary.rejected += 1
        summary.errors.push(rejeicao.message)
      } else if (!(err instanceof ApiError) && !isNetworkLike(err)) {
        // Erro de montagem do DTO (outbox corrompido) — não reenviar
        const rejeicao: RejeicaoParteDiaria = {
          code: 'PARTE_INCOMPLETA',
          message: reason,
          linhaId: null,
          campo: null,
          corrigivel: !!item.draftState,
        }
        await db.out_parte_diaria.update(item.id, { syncStatus: 'REJEITADA', lastSyncAttempt: now, failureReason: reason, rejeicao })
        summary.rejected += 1
        summary.errors.push(reason)
      } else {
        await db.out_parte_diaria.update(item.id, {
          syncStatus: 'PENDING',
          lastSyncAttempt: now,
          failureReason: reason,
          retryCount: (item.retryCount ?? 0) + 1,
        })
        summary.failed += 1
        summary.errors.push(reason)
      }
    }
  }
  return summary
}

function isNetworkLike(err: unknown): boolean {
  return err instanceof TypeError || (err instanceof Error && /network|fetch/i.test(err.message))
}

function montarRejeicao(err: ApiError, podeReabrir: boolean): RejeicaoParteDiaria {
  const code = err.code ?? `HTTP_${err.status}`
  const details = (err.details && typeof err.details === 'object' ? err.details : {}) as Record<string, unknown>
  let message = err.message
  if (code === 'VALIDATION_ERROR') {
    const campos = Object.keys(details)
    message = campos.length
      ? `Dados inválidos no envio (${campos.join(', ')}). Corrija e salve de novo.`
      : 'Dados inválidos no envio. Corrija e salve de novo.'
  }
  return {
    code,
    message,
    linhaId: typeof details.linhaId === 'string' ? details.linhaId : null,
    campo: typeof details.campo === 'string' ? details.campo : null,
    corrigivel: podeReabrir && !RECUSAS_SEM_CORRECAO_PD.includes(code),
  }
}

function buildMveDto(item: OutParteDiaria): LancamentoMveDto {
  if (!item.mve || item.mve.linhas.length === 0) throw new Error('Parte diária sem linhas no outbox')
  return {
    id: item.id,
    mveId: item.mveId,
    dtMovimentacao: item.dt,
    capturedAt: item.capturedAt,
    modo: item.mve.modo,
    horimetroInicialEsperado: item.mve.horimetroInicialEsperado,
    linhas: item.mve.linhas.map((l) => ({
      id: l.id,
      obraId: l.obraId,
      servicoExecutadoId: l.servicoExecutadoId,
      equipeId: l.equipeId,
      ficha: l.ficha,
      medicaoInicio: l.medicaoInicio,
      medicaoTermino: l.medicaoTermino,
      chapa: l.chapa,
      hInicio: l.hInicio,
      hFim: l.hFim,
      observacao: l.observacao,
    })),
  }
}

function buildEppDto(item: OutParteDiaria): LancamentoEppDto {
  if (!item.epp) throw new Error('Parte diária de EPP sem período no outbox')
  return {
    id: item.id,
    mveId: item.mveId,
    obraId: item.epp.obraId,
    servicoExecutadoId: item.epp.servicoExecutadoId,
    dtInicial: item.epp.dtInicial,
    dtFinal: item.epp.dtFinal,
    observacao: item.epp.observacao,
    capturedAt: item.capturedAt,
  }
}
